import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { debounce } from 'lodash';
import axios from 'axios';
import { trackEvent } from '../utils/utils';
import { useAgentDetails } from '../utils/useAgentDetails';
import EnquiryModal from '../components/EnquiryModal';
import PropertySearchLiveDesktop from './PropertySearchLiveDesktop';
import PropertySearchLiveMobile from './PropertySearchLiveMobile';
import './PropertySearchLive.css';

const MOBILE_BREAKPOINT = 768;

// Whether the visitor arrived with a tracking token (URL `?token=` or a value
// previously persisted to localStorage). Used to gate the "no sign-up" copy.
const hasParamToken = () => {
  try {
    return Boolean(
      new URLSearchParams(window.location.search).get('token')
      || localStorage.getItem('param_token')
    );
  } catch (_) {
    return false;
  }
};

/**
 * Conversion-focused landing page: search an address → instant price estimate.
 * Shared data/search logic lives here; the actual layout is delegated to a
 * desktop- or mobile-optimised presentational component based on viewport.
 */
function PropertySearchLive() {
  const { agentDetails, branding } = useAgentDetails();
  const navigate = useNavigate();

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  const [showNoSignup] = useState(hasParamToken);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (agentDetails) {
      document.title = `What's My Property Worth? | ${agentDetails.agency_name_short}`;
    }
  }, [agentDetails]);

  // Clear any background image a previous page (e.g. AgentHome) set on <body>.
  useEffect(() => {
    document.body.style.backgroundImage = null;
    document.body.style.backgroundColor = null;
  }, []);

  const loadProperties = debounce((inputValue, callback) => {
    axios.get(`${window.api_host}/property-search?q=${inputValue}`)
      .then((response) => {
        const options = response.data.map((property) => ({
          label: property.address_text,
          value: property.id,
          slug: property.slug,
        }));
        callback(options);
      })
      .catch((error) => {
        console.error('Error loading property options:', error);
        callback([]);
      });
  }, 400);

  const goToProperty = (option) => {
    setIsSubmitting(true);
    const agentSlug = agentDetails.landing_page_slug;
    const path = `/property-profile/${option.slug}-${option.value}`;
    navigate(agentSlug === 'home-price' ? path : `${path}?agent=${agentSlug}`);
  };

  const handlePropertyChange = (option) => {
    setSelectedProperty(option);
    setMessage('');
    if (option) {
      trackEvent('submit_property_search', { source: isMobile ? 'psl_mobile' : 'psl_desktop' });
      goToProperty(option);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedProperty) {
      setMessage('Please select your property from the list to continue.');
      return;
    }
    trackEvent('submit_property_search', { source: isMobile ? 'psl_mobile' : 'psl_desktop' });
    goToProperty(selectedProperty);
  };

  if (!agentDetails) {
    return (
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const sharedProps = {
    agentDetails,
    branding,
    selectedProperty,
    loadProperties,
    handlePropertyChange,
    handleSubmit,
    message,
    isSubmitting,
    showNoSignup,
    onContactAgent: () => setShowEnquiry(true),
  };

  return (
    <>
      {isMobile
        ? <PropertySearchLiveMobile {...sharedProps} />
        : <PropertySearchLiveDesktop {...sharedProps} />}
      <EnquiryModal
        show={showEnquiry}
        onHide={() => setShowEnquiry(false)}
        agentDetails={agentDetails}
      />
    </>
  );
}

export default PropertySearchLive;
